<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import { reorderStepControlsAvailable } from 'src/stores/features/dev/reorder-step-controls';
	import { ButtonV2, IconButton, Text } from '@Shelly/shelly-design-system';
	import { isMobileReorderChrome, type ReorderStepVariant } from './reorder_step_helpers';

	export let variant: ReorderStepVariant = 'vertical';
	export let index: number;
	export let listLength: number;
	export let enabled = false;

	const dispatch = createEventDispatcher<{
		step: { index: number; delta: number; cardEl: HTMLElement };
	}>();

	let windowClientWidth: number;
	let rootEl: HTMLElement | undefined;

	$: isMobile = isMobileReorderChrome(windowClientWidth);
	$: show = enabled && $reorderStepControlsAvailable && isMobile && listLength > 1;
	$: canMovePrev = index > 0;
	$: canMoveNext = index < listLength - 1;
	$: position = index + 1;
	$: positionLabel = $format('label_reorder_position', {
		default: '{position} / {total}',
		values: { position, total: listLength },
	});
	$: positionAria = $format('aria_reorder_position', {
		default: 'Position {position} of {total}',
		values: { position, total: listLength },
	});

	const onStep = (event: Event, delta: number) => {
		event.stopPropagation();
		if (!rootEl) return;
		if (delta < 0 && !canMovePrev) return;
		if (delta > 0 && !canMoveNext) return;
		dispatch('step', { index, delta, cardEl: rootEl });
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<div
	class:reorder-wrap={show}
	class:reorder-wrap--inactive={!show}
	class:reorder-wrap--stack={show && variant === 'previousNext'}
	class={show ? 'd-flex gap-s width-full' : ''}
	bind:this={rootEl}
>
	<div class:reorder-wrap-card={show} class:flex-1={show} class:min-width-0={show}>
		<slot />
	</div>
	{#if show}
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<div
			class="reorder-controls d-flex flex-shrink-0 gap-s"
			class:reorder-controls--vertical={variant === 'vertical'}
			class:reorder-controls--horizontal={variant === 'horizontal'}
			class:reorder-controls--previous-next={variant === 'previousNext'}
			data-dnd-ignore
			on:mousedown|stopPropagation
			on:touchstart|stopPropagation
			role="presentation"
		>
			{#if variant === 'previousNext'}
				<div
					class="reorder-position reorder-position--wide d-flex align-items-center justify-content-center"
					role="status"
					aria-atomic="true"
					aria-label={positionAria}
				>
					{#key position}
						<Text
							element="span"
							styleType="body-extra-small"
							class="text-color-muted"
							noWrap
						>
							{positionLabel}
						</Text>
					{/key}
				</div>
				<div class="reorder-previous-next-buttons d-flex align-items-stretch gap-s width-full">
					<ButtonV2
						size="s"
						variant="primary"
						icon="fas fa-chevron-left"
						disabled={!canMovePrev}
						aria-label={$format('label_reorder_previous', { default: 'Previous' })}
						on:click={(e) => onStep(e, -1)}
					>
						{$format('label_reorder_previous', { default: 'Previous' })}
					</ButtonV2>
					<ButtonV2
						size="s"
						variant="primary"
						icon="fas fa-chevron-right"
						disabled={!canMoveNext}
						aria-label={$format('label_reorder_next', { default: 'Next' })}
						on:click={(e) => onStep(e, 1)}
					>
						{$format('label_reorder_next', { default: 'Next' })}
					</ButtonV2>
				</div>
			{:else}
				<IconButton
					variant="muted"
					icon="fas fa-chevron-up"
					disabled={!canMovePrev}
					aria-label={$format('aria_move_item_up', { default: 'Move item up' })}
					on:click={(e) => onStep(e, -1)}
				/>
				<div
					class="reorder-position d-flex align-items-center justify-content-center"
					role="status"
					aria-atomic="true"
					aria-label={positionAria}
				>
					{#key position}
						<Text
							element="span"
							styleType="body-extra-small"
							class="text-color-muted"
							noWrap
						>
							{positionLabel}
						</Text>
					{/key}
				</div>
				<IconButton
					variant="muted"
					icon="fas fa-chevron-down"
					disabled={!canMoveNext}
					aria-label={$format('aria_move_item_down', { default: 'Move item down' })}
					on:click={(e) => onStep(e, 1)}
				/>
			{/if}
		</div>
	{/if}
</div>

<style>
	.reorder-wrap {
		align-items: stretch;
	}

	/* display:contents cannot be expressed with DS utilities */
	.reorder-wrap--inactive {
		display: contents;
	}

	.reorder-wrap--stack {
		flex-direction: column;
		align-items: stretch;
	}

	.reorder-controls {
		touch-action: manipulation;
	}

	.reorder-controls--vertical {
		flex-direction: column;
		justify-content: center;
	}

	.reorder-controls--horizontal {
		flex-direction: row;
		align-items: center;
	}

	.reorder-controls--previous-next {
		flex-direction: column;
		align-items: stretch;
		width: 100%;
	}

	.reorder-previous-next-buttons :global(.button-v2) {
		flex: 1;
	}

	.reorder-position {
		min-width: 3.25em;
		pointer-events: none;
		font-variant-numeric: tabular-nums;
	}

	.reorder-position--wide {
		width: 100%;
		min-width: 0;
	}

	.reorder-position :global(span) {
		animation: reorder-position-in 220ms ease-out;
	}

	@keyframes reorder-position-in {
		from {
			opacity: 0.45;
			transform: scale(0.92);
		}

		to {
			opacity: 1;
			transform: scale(1);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.reorder-position :global(span) {
			animation: none;
		}
	}
</style>
